// src/api/api.js
const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : '';

// Cookie helpers - tokens shared across tabs
const setCookie = (name, value, days = 7) => {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // Always use Secure flag (required for SameSite=None)
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  console.log(`[Cookie] Set ${name}:`, value.substring(0, 20) + '...');
  console.log(`[Cookie] All cookies after set:`, document.cookie);
};

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = decodeURIComponent(parts.pop().split(';').shift());
    console.log(`[Cookie] Got ${name}:`, cookieValue ? cookieValue.substring(0, 20) + '...' : 'null');
    return cookieValue;
  }
  console.log(`[Cookie] ${name} not found. Available cookies:`, document.cookie);
  return null;
};

const removeCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;
  console.log(`[Cookie] Removed ${name}`);
};

// Fonction utilitaire pour les requêtes authentifiées
const authRequest = async (endpoint, options = {}) => {
  const token = getCookie('token');

  if (!token) {
    throw new Error('Aucun token d\'authentification trouvé');
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (response.status === 401 || response.status === 403) {
    removeCookie('token');
    removeCookie('user');
    window.location.href = '/login';
    return;
  }

  return response;
};

// Authentification
export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    return response;
  },

  register: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });
    return response;
  },
};

// Questions
export const questionsAPI = {
  getAll: async (filters = {}) => {
    // Construire la chaîne de requête à partir des filtres
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `/questions?${queryString}` : '/questions';

    const response = await authRequest(url);
    return response.json();
  },

  getById: async (id) => {
    const response = await authRequest(`/questions/${id}`);
    return response.json();
  },

  create: async (data) => {
    const cleanedData = {
      ...data,
      annee_reglementaire: data.annee_reglementaire ? parseInt(data.annee_reglementaire) : null,
      quiz_id: data.quiz_id ? parseInt(data.quiz_id) : null
    };
    
    const response = await authRequest('/questions', {
      method: 'POST',
      body: JSON.stringify(cleanedData),
    });
    return response.json();
  },

  update: async (id, data) => {
    const cleanedData = {
      ...data,
      annee_reglementaire: data.annee_reglementaire ? parseInt(data.annee_reglementaire) : null,
      formation_ids: data.formation_ids || [],
      domaine_expertise_ids: data.domaine_expertise_ids || []
    };

    const response = await authRequest(`/questions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(cleanedData),
    });
    return response.json();
  },

  remove: async (id) => {
    const response = await authRequest(`/questions/${id}`, {
      method: 'DELETE',
    });
    
    // Si la réponse n'est pas OK (ex: 400, 500), on lit l'erreur et on la lance
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `Erreur HTTP ${response.status}`);
    }
    
    return response;
  },

  checkDuplicate: async (intitule, excludeId = null) => {
    const params = new URLSearchParams({ intitule });
    if (excludeId) params.append('excludeId', excludeId);
    const response = await authRequest(`/questions/check-duplicate?${params}`);
    return response.json();
  },

  exportAll: async (filters = {}) => {
    const token = getCookie('token');

    if (!token) {
      throw new Error('Aucun token d\'authentification trouvé');
    }

    // Construire la chaîne de requête à partir des filtres
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `/questions/export?${queryString}` : '/questions/export';

    // Pour le téléchargement de fichiers, on utilise directement fetch
    const response = await fetch(`${API_BASE_URL}${url}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (response.status === 401 || response.status === 403) {
      // Token invalide ou expiré
      removeCookie('token');
      removeCookie('user');
      window.location.href = '/login';
      return;
    }

    // Vérifier si la réponse est une erreur
    if (!response.ok) {
      const errorText = await response.text(); // Lire le corps de la réponse
      try {
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.error || 'Erreur inconnue lors de l\'export');
      } catch (e) {
        // Si la réponse n'est pas du JSON, lever une erreur avec le texte brut
        throw new Error(errorText || `Erreur HTTP ${response.status} lors de l'export`);
      }
    }

    return response.blob(); // On retourne directement le blob
  },
};

// Question History
export const questionHistoryAPI = {
  getByQuestionId: async (questionId) => {
    const response = await authRequest(`/question-history/question/${questionId}`);
    return response.json();
  },

  getAll: async (filters = {}) => {
    // Construire la chaîne de requête à partir des filtres
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const queryString = queryParams.toString();
    const url = queryString ? `/question-history?${queryString}` : '/question-history';

    const response = await authRequest(url);
    return response.json();
  },
};

// Formations
export const formationsAPI = {
  getAll: async () => {
    const response = await authRequest('/formations');
    return response.json();
  },

  create: async (data) => {
    const response = await authRequest('/formations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id, data) => {
    const response = await authRequest(`/formations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  remove: async (id) => {
    const response = await authRequest(`/formations/${id}`, { method: 'DELETE' });
    return response;
  },
};

// Programme pédagogique
export const programmeAPI = {
  get: async (formationId) => {
    const response = await authRequest(`/formations/${formationId}/programme`);
    return response.json();
  },

  createChapitre: async (formationId, data) => {
    const response = await authRequest(`/formations/${formationId}/chapitres`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateChapitre: async (chapitreId, data) => {
    const response = await authRequest(`/formations/chapitres/${chapitreId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteChapitre: async (chapitreId) => {
    await authRequest(`/formations/chapitres/${chapitreId}`, { method: 'DELETE' });
  },

  createModule: async (chapitreId, data) => {
    const response = await authRequest(`/formations/chapitres/${chapitreId}/modules`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateModule: async (moduleId, data) => {
    const response = await authRequest(`/formations/modules/${moduleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteModule: async (moduleId) => {
    await authRequest(`/formations/modules/${moduleId}`, { method: 'DELETE' });
  },

  createActivite: async (moduleId, data) => {
    const response = await authRequest(`/formations/modules/${moduleId}/activites`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  updateActivite: async (activiteId, data) => {
    const response = await authRequest(`/formations/activites/${activiteId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  deleteActivite: async (activiteId) => {
    await authRequest(`/formations/activites/${activiteId}`, { method: 'DELETE' });
  },
};


// Plateformes
export const plateformesAPI = {
  getAll: async () => {
    const response = await authRequest('/plateformes');
    return response.json();
  },

  getById: async (id) => {
    const response = await authRequest(`/plateformes/${id}`);
    return response.json();
  },

  create: async (data) => {
    const response = await authRequest('/plateformes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id, data) => {
    const response = await authRequest(`/plateformes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  remove: async (id) => {
    const response = await authRequest(`/plateformes/${id}`, {
      method: 'DELETE',
    });
    return response;
  },

  getByQuestionId: async (questionId) => {
    const response = await authRequest(`/plateformes/question/${questionId}`);
    return response.json();
  },
};

// Modules
export const domainesExpertiseAPI = {
  getAll: async () => {
    const response = await authRequest('/domaines-expertise');
    return response.json();
  },

  getById: async (id) => {
    const response = await authRequest(`/domaines-expertise/${id}`);
    return response.json();
  },

  create: async (data) => {
    const response = await authRequest('/domaines-expertise', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id, data) => {
    const response = await authRequest(`/domaines-expertise/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  remove: async (id) => {
    const response = await authRequest(`/domaines-expertise/${id}`, {
      method: 'DELETE',
    });
    return response;
  },
};

// Dashboard
export const dashboardAPI = {
  getStats: async () => {
    const response = await authRequest('/dashboard/stats');
    return response.json();
  },
};

// Ressources documentaires
export const ressourcesAPI = {
  getAll: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    const qs = queryParams.toString();
    const response = await authRequest(qs ? `/ressources?${qs}` : '/ressources');
    return response.json();
  },

  getDomaines: async () => {
    const response = await authRequest('/ressources/domaines');
    return response.json();
  },

  create: async (data) => {
    const response = await authRequest('/ressources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  update: async (id, data) => {
    const response = await authRequest(`/ressources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.json();
  },

  remove: async (id) => {
    const response = await authRequest(`/ressources/${id}`, { method: 'DELETE' });
    return response;
  },
};

// Partage ressources
export const partageAPI = {
  getToken: async () => {
    const response = await authRequest('/partage/ressources');
    return response.json();
  },
  createToken: async () => {
    const response = await authRequest('/partage/ressources', { method: 'POST' });
    return response.json();
  },
  revokeToken: async () => {
    await authRequest('/partage/ressources', { method: 'DELETE' });
  },
};

export const publicRessourcesAPI = {
  getAll: async (token) => {
    const response = await fetch(`${API_BASE_URL}/partage/public/ressources/${token}`);
    if (!response.ok) throw new Error('Lien invalide');
    return response.json();
  },
};

// Partage d'une ressource individuelle
export const partageRessourceAPI = {
  getToken: async (ressourceId) => {
    const response = await authRequest(`/partage/ressource/${ressourceId}`);
    return response.json();
  },
  createToken: async (ressourceId) => {
    const response = await authRequest(`/partage/ressource/${ressourceId}`, { method: 'POST' });
    return response.json();
  },
  revokeToken: async (ressourceId) => {
    await authRequest(`/partage/ressource/${ressourceId}`, { method: 'DELETE' });
  },
};

export const publicRessourceAPI = {
  get: async (token) => {
    const response = await fetch(`${API_BASE_URL}/partage/public/ressource/${token}`);
    if (!response.ok) throw new Error('Lien invalide');
    return response.json();
  },
};

// LearnDash Sync
export const learndashSyncAPI = {
  syncQuestion: async (questionId) => {
    const response = await authRequest(`/learndash-sync/sync/${questionId}`, {
      method: 'POST',
    });
    return response.json();
  },

  syncQuestionToPlatform: async (questionId, platformConfig) => {
    const response = await authRequest(`/learndash-sync/sync/${questionId}?platformId=${platformConfig.id}`, {
      method: 'POST',
    });
    return response.json();
  },

  syncMultipleQuestions: async (questionIds) => {
    const response = await authRequest('/learndash-sync/sync-multiple', {
      method: 'POST',
      body: JSON.stringify({ questionIds }),
    });
    return response.json();
  },

  unsyncQuestion: async (questionId, platformInstance = null) => {
    const params = platformInstance ? `?platformInstance=${encodeURIComponent(platformInstance)}` : '';
    const response = await authRequest(`/learndash-sync/sync/${questionId}${params}`, {
      method: 'DELETE',
    });
    return response.json();
  },

  getSyncStatus: async (questionId) => {
    const response = await authRequest(`/questions/${questionId}/sync-status`);
    return response.json();
  },
};